import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VirtualTable } from '../../../ui-kit/VirtualTable';
import { CustomerService, queryCustomers } from '../services/CustomerService';
import { RefreshCw, Download, ChevronLeft, ChevronRight, Target } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdvancedFilterPanel, { FILTER_DEFAULTS } from './AdvancedFilterPanel';
import { differenceInDays, parseISO, formatDistanceToNow } from 'date-fns';
import { csvCell } from '../../../shared/utils/csv';
import { db } from '../../../integrations/firebase';
import { ref, get } from 'firebase/database';

// Last-10-digit mobile key, so ticket "+91 80752..." matches customer "80752...".
const norm10 = (m) => String(m || '').replace(/\D/g, '').slice(-10);
const CLOSED_TICKET_STATUSES = new Set(['resolved', 'closed']);

const CustomerList = () => {
    const navigate = useNavigate();
    const [rawData, setRawData] = useState([]); // Store fetched data
    const [filteredData, setFilteredData] = useState([]); // Store filtered data
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // Pagination State
    const [limit, setLimit] = useState(50);
    const [page, setPage] = useState(1);
    const [lastKeyStack, setLastKeyStack] = useState([null]);

    const [error, setError] = useState(null);

    // Mobiles (last-10) of customers with at least one OPEN ticket — for the
    // "Has Open Ticket" filter. Loaded once; tickets are a modest collection.
    const [openTicketMobiles, setOpenTicketMobiles] = useState(() => new Set());
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                // Just the phone numbers (~2 KB). This used to pull the whole
                // `tickets` tree (~0.94 MB) into the browser to answer a yes/no
                // question per customer.
                const { authHeaders } = await import('../../../core/api/authHeaders');
                const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
                const r = await fetch(`${PROXY}/api/complaints/open-mobiles`, { headers: await authHeaders() });
                if (!active || !r.ok) return;
                const j = await r.json();
                if (!active || !Array.isArray(j.mobiles)) return;
                setOpenTicketMobiles(new Set(j.mobiles.map(norm10)));
            } catch (e) { /* non-fatal: filter simply matches nobody */ }
        })();
        return () => { active = false; };
    }, []);

    // Fetch Data — the SERVER filters, sorts and paginates across every customer.
    // (This used to pull one page of 50 and filter those 50 in the browser, which is why
    // "10+ orders" reported 0 results against 111k customers.)
    const [activeFilters, setActiveFilters] = useState({});
    const [pages, setPages] = useState(1);
    const [indexSize, setIndexSize] = useState(0);   // how many customers the filter ran over
    const [sort, setSort] = useState('mobile');
    const [dir, setDir] = useState('desc');

    // Read through a ref so changing the sort does not rebuild fetchData (and so the
    // effect that watches it cannot loop).
    const sortRef = useRef({ sort: 'mobile', dir: 'desc' });
    sortRef.current = { sort, dir };

    const fetchData = useCallback(async (pageNo = 1, filters = activeFilters) => {
        setLoading(true);
        setError(null);
        try {
            const res = await queryCustomers(filters, pageNo, limit, sortRef.current.sort, sortRef.current.dir);
            setRawData(res.rows);
            setFilteredData(res.rows);
            setTotalCount(res.total);
            setPages(res.pages);
            setIndexSize(res.indexSize || 0);
            setPage(res.page);
        } catch (e) {
            console.error('Failed to fetch customers', e);
            setError(e.message || 'Failed to load customers.');
        } finally {
            setLoading(false);
        }
    }, [limit, activeFilters]);

    const [searchParams, setSearchParams] = useSearchParams();

    // Initial Load & URL Sync.
    // The filters, sort and page all live in the URL, so going into a customer and
    // pressing Back restores exactly the view you left — and the link can be shared or
    // opened in a new tab.
    // Derived from the panel's own definition so a filter can never be added there
    // and silently dropped from the URL, the shared link or the server query.
    const FILTER_KEYS = Object.keys(FILTER_DEFAULTS);

    const urlFilters = () => {
        const f = {};
        FILTER_KEYS.forEach(k => { const v = searchParams.get(k); if (v) f[k] = v; });
        return f;
    };

    const writeUrl = (filters, pageNo, sortKey, sortDir) => {
        const next = new URLSearchParams();
        Object.entries(filters || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '' && v !== 'all') next.set(k, String(v));
        });
        if (pageNo > 1) next.set('page', String(pageNo));
        if (sortKey && sortKey !== 'mobile') next.set('sort', sortKey);
        if (sortDir && sortDir !== 'desc') next.set('dir', sortDir);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const f = urlFilters();
        const p = Number(searchParams.get('page')) || 1;
        const sk = searchParams.get('sort') || 'mobile';
        const sd = searchParams.get('dir') || 'desc';
        setActiveFilters(f);
        setSort(sk); setDir(sd);
        sortRef.current = { sort: sk, dir: sd };
        fetchData(p, f);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit]);

    const handleApplyFilters = (filters) => {
        // Straight to the server: every filter below is evaluated over the whole base.
        setActiveFilters(filters);
        writeUrl(filters, 1, sort, dir);
        fetchData(1, filters);
    };

    /** Click a column to sort; click it again to flip the direction. */
    const handleSort = (key) => {
        const nextDir = sort === key && dir === 'desc' ? 'asc' : 'desc';
        setSort(key); setDir(nextDir);
        sortRef.current = { sort: key, dir: nextDir };
        writeUrl(activeFilters, 1, key, nextDir);
        fetchData(1, activeFilters);
    };

    const handleNext = () => {
        if (page >= pages) return;
        writeUrl(activeFilters, page + 1, sort, dir);
        fetchData(page + 1);
    };

    const handlePrev = () => {
        if (page <= 1) return;
        writeUrl(activeFilters, page - 1, sort, dir);
        fetchData(page - 1);
    };

    const handleRowClick = (customer, e) => {
        // Fallback chain: some records (website-order stubs) have no primaryMobile;
        // the record id/key IS the 10-digit mobile.
        const raw = customer.primaryMobile || customer.mobile || customer.id || customer.key || '';
        const safeMobile = String(raw).replace(/[.#$[\]/]/g, '_');
        if (!safeMobile) { alert('This customer record has no mobile number to open a profile with.'); return; }
        // Ctrl/Cmd-click or middle-click opens the profile in a new tab, so the filtered
        // list stays exactly where it is. A plain click navigates; the filters are in the
        // URL, so Back returns to the same filtered page.
        if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
            window.open(`${window.location.origin}/customers/${safeMobile}`, '_blank', 'noopener');
            return;
        }
        navigate(`/customers/${safeMobile}`);
    };

    // against formula injection in spreadsheet apps.
    /**
     * Export EVERY customer the current filters match — not the 50 rows on screen.
     * The server streams the CSV straight from its index, so a 111k-row export costs
     * the browser nothing but the download.
     */
    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        if (!totalCount) { alert('No customers to export.'); return; }
        setExporting(true);
        try {
            const { exportCustomersCsv } = await import('../services/CustomerService');
            await exportCustomersCsv({ ...activeFilters, sort, dir });
        } catch (e) {
            alert(`Export failed: ${e.message}`);
        } finally {
            setExporting(false);
        }
    };

    /**
     * Add the filtered customers to a campaign AS A BATCH. A campaign is created first
     * (or picked from the list); anyone already in it is reported as "already in batch N"
     * instead of being added twice, so overlapping filters never double-target a customer.
     */
    const [campaigning, setCampaigning] = useState(false);
    // ---- Assign to Telecaller (capability-gated server-side as well) ----
    const [canAssign, setCanAssign] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);
    const [tcAgents, setTcAgents] = useState([]);
    const [assignForm, setAssignForm] = useState({ agentUid: '', campaign: 'reorder', priority: 'normal', dueAt: '', language: '', note: '', scope: 'page' });
    const [assignPreview, setAssignPreview] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [assignResult, setAssignResult] = useState(null);
    const tcApi = useCallback(async (path, body) => {
        const { authHeaders } = await import('../../../core/api/authHeaders');
        const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
        const r = await fetch(`${PROXY}${path}`, {
            method: body ? 'POST' : 'GET',
            headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(await authHeaders()) },
            body: body ? JSON.stringify(body) : undefined,
        });
        return r.json();
    }, []);
    useEffect(() => {
        tcApi('/api/tc/me').then(j => setCanAssign(!!j?.caps?.includes('assign'))).catch(() => {});
    }, [tcApi]);
    useEffect(() => {
        if (assignOpen && !tcAgents.length) tcApi('/api/tc/agents').then(j => setTcAgents(j.agents || [])).catch(() => {});
    }, [assignOpen, tcAgents.length, tcApi]);

    const assignKeys = useCallback(async () => {
        if (assignForm.scope === 'page') return filteredData.map(r => norm10(r.primaryMobile || r.firebaseKey)).filter(k => k.length === 10);
        // Entire filtered set: re-run the active filter server-side at the max
        // page size. Beyond 1000, re-running the assignment continues (the
        // bulk op skips already-assigned keys) — stated in the modal.
        const res = await queryCustomers(activeFilters, 1, 1000, sort, dir);
        return (res.rows || []).map(r => norm10(r.primaryMobile)).filter(k => k.length === 10);
    }, [assignForm.scope, filteredData, activeFilters, sort, dir]);

    const runAssignPreview = async () => {
        setAssigning(true); setAssignResult(null);
        try {
            const keys = await assignKeys();
            const p = await tcApi('/api/tc/assign/preview', { keys });
            setAssignPreview({ ...p, keys });
        } catch (e) { alert(e.message || 'Preview failed'); }
        finally { setAssigning(false); }
    };
    const runAssign = async () => {
        if (!assignForm.agentUid) { alert('Pick the telecaller first.'); return; }
        if (!assignPreview?.eligible?.length) { alert('Nothing eligible to assign — run the preview.'); return; }
        setAssigning(true);
        try {
            const agent = tcAgents.find(a => a.uid === assignForm.agentUid);
            const out = await tcApi('/api/tc/assign/bulk', {
                keys: assignPreview.eligible,
                agentUid: assignForm.agentUid, agentName: agent?.name || '',
                campaign: assignForm.campaign, priority: assignForm.priority,
                dueAt: assignForm.dueAt ? new Date(assignForm.dueAt).getTime() : 0,
                language: assignForm.language, note: assignForm.note,
                opId: `asg_${assignForm.agentUid}_${new Date().toISOString().slice(0, 10)}_${assignPreview.eligible.length}`,
            });
            setAssignResult(out.ok ? `✅ Assigned ${out.done} customer(s) to ${agent?.name || 'the agent'}.` : `⚠ ${out.error || 'Assignment failed'}`);
            setAssignPreview(null);
        } catch (e) { setAssignResult(`⚠ ${e.message}`); }
        finally { setAssigning(false); }
    };

    const [campaignOpen, setCampaignOpen] = useState(false);
    const [campaignList, setCampaignList] = useState([]);
    const [campaignPick, setCampaignPick] = useState('');     // '' = create new
    const [campaignName, setCampaignName] = useState('');
    const [campaignResult, setCampaignResult] = useState(null);

    const openCampaignModal = async () => {
        if (!totalCount) { alert('No customers match the current filters.'); return; }
        setCampaignResult(null); setCampaignOpen(true); setCampaignPick(''); setCampaignName('');
        try {
            const { listCampaigns } = await import('../services/CustomerService');
            setCampaignList(await listCampaigns());
        } catch { setCampaignList([]); }
    };

    const submitCampaign = async () => {
        if (!campaignPick && !campaignName.trim()) { alert('Pick a campaign or name a new one.'); return; }
        setCampaigning(true);
        try {
            const { addToCampaign } = await import('../services/CustomerService');
            const r = await addToCampaign({
                campaignId: campaignPick || '',
                name: campaignPick ? '' : campaignName.trim(),
                filters: { ...activeFilters, sort, dir },
                source: 'customer-intelligence',
            });
            setCampaignResult(r);
        } catch (e) {
            alert(`Could not add to campaign: ${e.message}`);
        } finally {
            setCampaigning(false);
        }
    };



    const getRelationshipAge = (row) => {
        if (!row.firstOrderDate && !row.createdAt) return 'New';
        const date = row.firstOrderDate || row.createdAt;
        try {
            return formatDistanceToNow(parseISO(date), { addSuffix: false });
        } catch (e) {
            return 'Unknown';
        }
    };

    const columns = [
        { header: 'Name', key: 'name', sortKey: 'name', width: 200, className: 'font-medium text-slate-800', render: (row) => String(row.name || '') },
        { header: 'Mobile', key: 'primaryMobile', sortKey: 'mobile', width: 120, render: (row) => String(row.primaryMobile || '') },
        { header: 'City', key: 'city', sortKey: 'city', width: 120, render: (row) => String(row.city || '') },
        {
            header: 'Relationship', key: 'relationship', width: 120, render: (row) => (
                <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded">
                    {getRelationshipAge(row)}
                </span>
            )
        },
        {
            header: 'Addresses', key: 'addresses', width: 150, render: (row) => {
                const count = row.addressCount ?? (row.addresses ? row.addresses.length : 0);
                return count > 0 ? (
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-slate-700 truncate" title={row.city || ''}>
                            {(row.addresses && row.addresses[0] && row.addresses[0].city) || row.city || 'Main Address'}
                        </span>
                        {count > 1 && <span className="text-[10px] text-slate-400">+{count - 1} more</span>}
                    </div>
                ) : <span className="text-slate-400 text-xs">-</span>;
            }
        },
        { header: 'Orders', key: 'totalOrders', sortKey: 'orders', width: 80, className: 'text-center', render: (row) => String(row.totalOrders || 0) },
        { header: 'Total Spent', key: 'totalSpent', sortKey: 'spent', width: 100, render: (row) => `₹${(Number(row.totalSpent) || 0).toLocaleString()}` },
        {
            header: 'Health Score', key: 'healthScore', sortKey: 'health', width: 100, render: (row) => {
                const score = Number(row.healthScore) || 0;
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${score > 80 ? 'bg-green-100 text-green-700' :
                        score > 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                        }`}>
                        {score}
                    </span>
                );
            }
        },
        {
            header: 'Risk', key: 'churnRisk', width: 80, render: (row) => (
                <span className={`text-xs font-bold ${row.churnRisk === 'High' ? 'text-red-500' : 'text-slate-500'}`}>
                    {String(row.churnRisk || 'Low')}
                </span>
            )
        }
    ];

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Mobile Card Component
    const CustomerCard = ({ customer }) => (
        <div
            onClick={() => handleRowClick(customer)}
            className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm active:scale-95 transition-transform cursor-pointer space-y-4"
        >
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="text-lg font-bold text-slate-800">{customer.name}</h3>
                    <p className="text-sm text-slate-500 font-medium">{customer.primaryMobile}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${(Number(customer.healthScore) || 0) > 80 ? 'bg-green-100 text-green-700' :
                    (Number(customer.healthScore) || 0) > 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                    }`}>
                    {Number(customer.healthScore) || 0} Score
                </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider block mb-1">City</span>
                    <span className="font-medium text-slate-700 text-sm">{customer.city || 'N/A'}</span>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider block mb-1">Total Spent</span>
                    <span className="font-bold text-slate-800 text-sm">₹{(Number(customer.totalSpent) || 0).toLocaleString()}</span>
                </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${customer.churnRisk === 'High' ? 'bg-red-500' : 'bg-green-500'}`}></span>
                    <span className={`text-xs font-bold ${customer.churnRisk === 'High' ? 'text-red-600' : 'text-slate-600'}`}>
                        {customer.churnRisk || 'Low'} Risk
                    </span>
                </div>
                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    {customer.totalOrders || 0} Orders
                </span>
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            <AdvancedFilterPanel initialFilters={activeFilters} onApplyFilters={handleApplyFilters} />

            <div className="bg-white md:bg-transparent p-0 md:p-6 rounded-xl shadow-none md:shadow-sm border-none md:border border-slate-100 space-y-4">
                {/* Controls */}
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 md:p-0 rounded-xl border md:border-none border-slate-100 shadow-sm md:shadow-none">
                    <div className="text-sm text-slate-500">
                        {/* totalCount is the server's TRUE match count across every
                            customer — not the size of the page on screen. */}
                        {Object.keys(activeFilters).some(k => activeFilters[k] && activeFilters[k] !== 'all') ? (
                            <span>Filtered: <b>{totalCount.toLocaleString()}</b> of {}
                                <b>{Number(indexSize || 0).toLocaleString()}</b> customers</span>
                        ) : (
                            <span>Total Customers: <b>{totalCount.toLocaleString()}</b></span>
                        )}
                    </div>
                    <div className="flex gap-2 items-center w-full md:w-auto">
                        <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="flex-1 md:flex-none px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 outline-none cursor-pointer hover:border-purple-300 transition-colors"
                        >
                            <option value={10}>10 rows</option>
                            <option value={25}>25 rows</option>
                            <option value={50}>50 rows</option>
                            <option value={100}>100 rows</option>
                            <option value={250}>250 rows</option>
                            <option value={1000}>1000 rows</option>
                        </select>
                        <Button variant="secondary" icon={RefreshCw} onClick={() => fetchData(lastKeyStack[lastKeyStack.length - 1])} />
                        <Button variant="secondary" icon={Download} className="hidden md:flex" onClick={handleExport} disabled={exporting}>
                            {exporting ? 'Preparing…' : `Export ${Number(totalCount || 0).toLocaleString()}`}
                        </Button>
                        <Button variant="primary" icon={Target} className="hidden md:flex" onClick={openCampaignModal} disabled={campaigning} title="Add these filtered customers to a campaign as a new batch (duplicates are detected automatically)">
                            {campaigning ? 'Adding…' : 'Add to campaign'}
                        </Button>
                        {canAssign && (
                            <Button variant="secondary" className="hidden md:flex" onClick={() => setAssignOpen(true)}
                                title="Assign the filtered customers to a telecaller (with campaign, priority and due date)">
                                📞 Assign to Telecaller
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table or Cards */}
                {loading ? (
                    <div className="h-96 flex flex-col items-center justify-center text-slate-400 gap-3">
                        <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                        <p>Loading customers...</p>
                    </div>
                ) : error ? (
                    <div className="h-96 flex flex-col items-center justify-center text-red-500 gap-3">
                        <div className="text-lg font-bold">Error Loading Data</div>
                        <p>{error}</p>
                        <Button variant="secondary" onClick={() => fetchData(null)}>Retry</Button>
                    </div>
                ) : isMobile ? (
                    <div className="grid grid-cols-1 gap-4">
                        {filteredData.map((customer, idx) => (
                            <CustomerCard key={customer.id || idx} customer={customer} />
                        ))}
                    </div>
                ) : (
                    <VirtualTable
                        data={filteredData}
                        columns={columns}
                        height={600}
                        rowHeight={50}
                        onRowClick={handleRowClick}
                        sort={sort}
                        dir={dir}
                        onSort={handleSort}
                    />
                )}

                {/* Pagination Footer */}
                <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                    <div className="text-sm text-slate-500 font-medium">
                        Page {page} of {pages}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrev}
                            disabled={page === 1 || loading}
                            className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft size={20} className="text-slate-600" />
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={rawData.length < limit || loading}
                            className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight size={20} className="text-slate-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Assign-to-telecaller modal */}
            {assignOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !assigning && setAssignOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                            <span className="text-lg">📞</span>
                            <h3 className="font-bold text-slate-800">Assign customers to a telecaller</h3>
                        </div>
                        <div className="p-4 space-y-3 text-sm">
                            <div className="flex gap-3 text-xs">
                                <label className="flex items-center gap-1.5"><input type="radio" checked={assignForm.scope === 'page'} onChange={() => { setAssignForm(f => ({ ...f, scope: 'page' })); setAssignPreview(null); }} /> This page ({filteredData.length})</label>
                                <label className="flex items-center gap-1.5"><input type="radio" checked={assignForm.scope === 'filter'} onChange={() => { setAssignForm(f => ({ ...f, scope: 'filter' })); setAssignPreview(null); }} /> Entire filtered set ({Number(totalCount || 0).toLocaleString()}; runs 1000 at a time — repeat to continue)</label>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <label className="text-xs text-slate-500">Telecaller
                                    <select className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.agentUid} onChange={e => setAssignForm(f => ({ ...f, agentUid: e.target.value }))}>
                                        <option value="">— pick —</option>
                                        {tcAgents.map(a => <option key={a.uid} value={a.uid}>{a.name} ({a.role})</option>)}
                                    </select>
                                </label>
                                <label className="text-xs text-slate-500">Purpose / campaign
                                    <select className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.campaign} onChange={e => setAssignForm(f => ({ ...f, campaign: e.target.value }))}>
                                        {['reorder', 'winback', 'feedback', 'cod_confirmation', 'ndr', 'callback_requested'].map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs text-slate-500">Priority
                                    <select className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.priority} onChange={e => setAssignForm(f => ({ ...f, priority: e.target.value }))}>
                                        {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs text-slate-500">First contact by
                                    <input type="datetime-local" className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.dueAt} onChange={e => setAssignForm(f => ({ ...f, dueAt: e.target.value }))} />
                                </label>
                                <label className="text-xs text-slate-500">Preferred language
                                    <select className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.language} onChange={e => setAssignForm(f => ({ ...f, language: e.target.value }))}>
                                        <option value="">any</option>
                                        {['Malayalam', 'Tamil', 'Telugu', 'Kannada', 'Hindi', 'English'].map(l => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                </label>
                                <label className="text-xs text-slate-500">Note
                                    <input className="w-full px-2 py-1.5 border border-slate-200 rounded-lg" value={assignForm.note} onChange={e => setAssignForm(f => ({ ...f, note: e.target.value }))} placeholder="context for the caller" />
                                </label>
                            </div>
                            {assignPreview && (
                                <div className="text-xs rounded-lg bg-slate-50 p-3 space-y-0.5">
                                    <div>✅ Eligible: <b>{assignPreview.counts?.eligible ?? 0}</b></div>
                                    <div className="text-amber-700">↷ Already assigned: <b>{assignPreview.counts?.alreadyAssigned ?? 0}</b>{assignPreview.alreadyAssigned?.slice(0, 2).map(a => ` (${a.key} → ${a.agentName || a.agentUid})`).join('')}</div>
                                    <div className="text-red-600">⛔ Excluded (DND / not interested): <b>{assignPreview.counts?.excluded ?? 0}</b></div>
                                </div>
                            )}
                            {assignResult && <div className="text-xs rounded-lg bg-emerald-50 text-emerald-800 p-3">{assignResult}</div>}
                            <div className="flex justify-between items-center pt-1">
                                <button className="text-xs text-slate-500" onClick={() => !assigning && setAssignOpen(false)}>Close</button>
                                <div className="flex gap-2">
                                    <Button variant="secondary" onClick={runAssignPreview} disabled={assigning}>{assigning ? 'Working…' : '1 · Preview'}</Button>
                                    <Button variant="primary" onClick={runAssign} disabled={assigning || !assignPreview}>{assigning ? 'Assigning…' : `2 · Assign${assignPreview ? ` ${assignPreview.counts?.eligible ?? 0}` : ''}`}</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add-to-campaign (batch) modal */}
            {campaignOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => !campaigning && setCampaignOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                            <Target size={18} className="text-purple-600" />
                            <h3 className="font-bold text-slate-800">Add {Number(totalCount || 0).toLocaleString()} customers to a campaign</h3>
                        </div>

                        {!campaignResult ? (
                            <div className="p-4 space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-500">Campaign</label>
                                    <select value={campaignPick} onChange={e => setCampaignPick(e.target.value)}
                                        className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm">
                                        <option value="">➕ Create a new campaign…</option>
                                        {campaignList.map(c => (
                                            <option key={c.offerId} value={c.offerId}>{c.title} — {c.targeted} customers{c.type === 'pending' ? ' (no offer yet)' : ''}</option>
                                        ))}
                                    </select>
                                </div>
                                {!campaignPick && (
                                    <div>
                                        <label className="text-xs font-bold text-slate-500">New campaign name</label>
                                        <input value={campaignName} onChange={e => setCampaignName(e.target.value)} autoFocus
                                            placeholder="e.g. Diwali win-back — at risk customers"
                                            className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                                    </div>
                                )}
                                <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2.5">
                                    These customers are added as the <b>next batch</b>. Anyone already in this campaign is skipped and reported — so overlapping filters never target the same customer twice. You attach the coupon / BOGO offer afterwards in <b>Quick Order → Campaign results</b>.
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button variant="secondary" onClick={() => setCampaignOpen(false)} disabled={campaigning}>Cancel</Button>
                                    <Button variant="primary" onClick={submitCampaign} disabled={campaigning}>{campaigning ? 'Adding…' : 'Add as batch'}</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 space-y-3">
                                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                                    <div className="font-bold text-emerald-800">
                                        {campaignResult.added > 0 ? `✅ Batch ${campaignResult.batch}: ${campaignResult.added.toLocaleString()} customers added` : 'Nothing new to add'}
                                    </div>
                                    <div className="text-[12px] text-emerald-700 mt-0.5">Campaign “{campaignResult.name}” now has {Number(campaignResult.totalNow || 0).toLocaleString()} customers.</div>
                                </div>
                                <div className="text-sm text-slate-600 space-y-1.5">
                                    <div>• Selected by this filter: <b>{Number(campaignResult.selected || 0).toLocaleString()}</b></div>
                                    {campaignResult.duplicateCount > 0 && (
                                        <div className="text-amber-700">• Skipped — <b>{campaignResult.duplicateCount.toLocaleString()}</b> already in this campaign
                                            {campaignResult.duplicates?.length > 0 && <span className="text-[11px] text-amber-600"> (e.g. {campaignResult.duplicates.slice(0, 3).map(d => `${d.mobile} in batch ${d.batch}`).join(', ')})</span>}
                                        </div>
                                    )}
                                    {/* These customers WERE added. Being in another campaign no
                                        longer blocks membership — it only means the storefront
                                        keeps showing them the discount they already had, since a
                                        customer is shown one offer at a time. */}
                                    {campaignResult.offerBlockedCount > 0 && (
                                        <div className="text-amber-700">• Added, but <b>{campaignResult.offerBlockedCount.toLocaleString()}</b> will keep seeing their current offer until it ends
                                            {campaignResult.offerBlocked?.length > 0 && <span className="text-[11px] text-amber-600"> (e.g. {campaignResult.offerBlocked.slice(0, 2).map(d => `${d.mobile} → ${d.title}`).join(', ')})</span>}
                                        </div>
                                    )}
                                    {/* Dropped by the campaign's audience-exclusion setting — these
                                        people are members of a campaign this one must never re-target. */}
                                    {campaignResult.excludedCount > 0 && (
                                        <div className="text-rose-700">• Excluded — <b>{campaignResult.excludedCount.toLocaleString()}</b> are in campaign(s) this campaign is set to avoid
                                            {campaignResult.excludedByCampaign?.length > 0 && (
                                                <span className="text-[11px] text-rose-600"> ({campaignResult.excludedByCampaign.map(x => `${x.count.toLocaleString()} in “${x.title}”`).join(', ')})</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end"><Button variant="primary" onClick={() => { setCampaignOpen(false); setCampaignResult(null); }}>Done</Button></div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerList;
